
// Separating Axis Theorem for two convex polygons (arrays of world-space
// points). Returns the minimum translation vector needed to push them apart -
// a unit normal (pointing from A toward B) and the penetration depth - or null
// if they do not overlap. Used to resolve asteroid-vs-asteroid collisions with
// the real rock shapes, getting both an accurate bounce direction and a clean
// separation each frame.
export function polygonMTV(pointsA: Phaser.Geom.Point[], pointsB: Phaser.Geom.Point[]): { nx: number, ny: number, depth: number } {
    let minOverlap = Infinity;
    let mtvx = 0;
    let mtvy = 0;

    const polys = [pointsA, pointsB];
    for (let pi = 0; pi < polys.length; pi++) {
        const pts = polys[pi];
        for (let i = 0; i < pts.length; i++) {
            const p1 = pts[i];
            const p2 = pts[(i + 1) % pts.length];

            // Axis = unit normal of this edge.
            let ax = -(p2.y - p1.y);
            let ay = (p2.x - p1.x);
            const len = Math.sqrt(ax * ax + ay * ay) || 1;
            ax /= len;
            ay /= len;

            // Project both polygons onto the axis.
            let minA = Infinity, maxA = -Infinity;
            for (let k = 0; k < pointsA.length; k++) {
                const proj = pointsA[k].x * ax + pointsA[k].y * ay;
                if (proj < minA) minA = proj;
                if (proj > maxA) maxA = proj;
            }
            let minB = Infinity, maxB = -Infinity;
            for (let k = 0; k < pointsB.length; k++) {
                const proj = pointsB[k].x * ax + pointsB[k].y * ay;
                if (proj < minB) minB = proj;
                if (proj > maxB) maxB = proj;
            }

            const overlap = Math.min(maxA, maxB) - Math.max(minA, minB);
            if (overlap <= 0) {
                return null; // Separating axis found - no collision.
            }
            if (overlap < minOverlap) {
                minOverlap = overlap;
                mtvx = ax;
                mtvy = ay;
            }
        }
    }

    // Orient the normal from A's centre toward B's centre.
    let cax = 0, cay = 0, cbx = 0, cby = 0;
    for (let k = 0; k < pointsA.length; k++) { cax += pointsA[k].x; cay += pointsA[k].y; }
    for (let k = 0; k < pointsB.length; k++) { cbx += pointsB[k].x; cby += pointsB[k].y; }
    cax /= pointsA.length; cay /= pointsA.length;
    cbx /= pointsB.length; cby /= pointsB.length;
    if ((cbx - cax) * mtvx + (cby - cay) * mtvy < 0) {
        mtvx = -mtvx;
        mtvy = -mtvy;
    }

    return { nx: mtvx, ny: mtvy, depth: minOverlap };
}

// Create a simple clickable text button (with a hover highlight) for the menu
// screens. Phaser's Scale Manager transforms pointer coords, so clicks land
// correctly even though the canvas is scaled to fit.
export function makeButton(scene: Phaser.Scene, x: number, y: number, label: string, onClick: () => void): Phaser.GameObjects.Text {
    const button = scene.add.text(x, y, label, {
        fontFamily: 'Arial',
        fontSize: '22px',
        color: '#cfe8ff',
        backgroundColor: '#1b2a44',
        padding: { x: 16, y: 8 }
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    button.on('pointerover', () => button.setColor('#ffffff'));
    button.on('pointerout', () => button.setColor('#cfe8ff'));
    button.on('pointerdown', onClick);

    return button;
}


// Fire `onInput` once on the next key press OR gamepad button press. Used by the
// "press any key" prompts (splash / credits / game-over) so a controller works
// there too, not just the keyboard. Callers wrap this in a short delayedCall so
// an input still held from the previous screen doesn't trigger it immediately.
export function onAnyInputOnce(scene: Phaser.Scene, onInput: () => void): void {
    let fired = false;
    const fire = () => { if (fired) { return; } fired = true; onInput(); };
    scene.input.keyboard.once('keydown', fire);
    // Only let a controller button advance once Web Audio is already unlocked.
    // The first interaction has to be a key (or click) so the browser unlocks
    // audio - a gamepad button doesn't count as the required "user gesture", so
    // a cold start on the controller would begin play silently (esp. on
    // Chromium/Edge). Once unlocked, the controller works everywhere (restart
    // after game over, etc.).
    if (scene.input.gamepad && !scene.sound.locked) {
        scene.input.gamepad.once('down', fire); // any controller button
    }
}

// Persist the player's volume settings across page reloads. localStorage can be
// unavailable (private mode, file://, blocked cookies), so every access is
// wrapped - a failure just falls back to defaults / no-op.
export const SETTINGS_KEY: string = 'spacegame.settings';

export function loadSettings(): { [k: string]: number } {
    try {
        const raw = window.localStorage.getItem(SETTINGS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (e) {
        return {};
    }
}

export function saveSetting(key: string, value: number): void {
    try {
        const settings = loadSettings();
        settings[key] = value;
        window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
        // Storage unavailable - settings just won't persist this session.
    }
}
